<i18n locale="en">
{
  "placeholders": {
    "fallback": "Select a value"
  },
  "labels": {
    "empty": "No options found"
  }
}
</i18n>

<template>
  <SelectRoot
    v-model="modelValue"
    class="own-select"
    :disabled="disabled"
    :multiple="multiple"
  >
    <SelectTrigger
      :id="accessibilityId"
      :aria-label="ariaLabel"
      :data-testid="accessibilityId ?? 'own-select-trigger'"
      class="own-select__trigger rounded-xl p-3"
      :class="[
        size === 'small' && 'own-select__trigger--small',
        error && 'own-select__trigger--error',
        disabled && 'own-select__trigger--disabled',
      ]"
      :aria-invalid="error"
      :aria-disabled="disabled"
    >
      <div class="align-center w-full flex-row justify-between gap-2">
        <div
          class="own-select__trigger-text-container align-center flex-row gap-2"
        >
          <slot name="trigger-left" :item="selectedValue">
            <Component
              :is="selectedValue?.icon"
              v-if="selectedValue?.icon"
              class="flex-shrink-0"
              size="16"
            />
          </slot>

          <SelectValue
            class="text--truncate"
            :placeholder="placeholder ?? t('placeholders.fallback')"
          >
            {{
              selectedValue?.label || placeholder || t('placeholders.fallback')
            }}
          </SelectValue>
        </div>

        <PhCaretDown
          class="text-color-primary flex-shrink-0"
          size="16"
          weight="bold"
        />
      </div>
    </SelectTrigger>

    <SelectPortal>
      <SelectContent
        class="own-select__content"
        :class="[
          compact && 'own-select__content--compact',
          size === 'small' && 'own-select__content--small',
        ]"
        position="popper"
        side="bottom"
        :side-offset="8"
        :avoid-collisions="true"
        :collision-padding="12"
        sticky="always"
        :style="{
          '--max-content-height': maxContentHeight,
          minWidth: contentMinWidth,
        }"
      >
        <SelectViewport class="own-select__viewport">
          <template v-if="options.length > 0">
            <SelectGroup v-for="group in optionGroups" :key="group.key">
              <SelectLabel v-if="group.label" class="own-select__group-label">
                {{ group.label }}
              </SelectLabel>
              <SelectItem
                v-for="option in group.options"
                :key="option.value"
                :value="option.value.toString()"
                class="own-select__item"
                :disabled="option.disabled"
                :data-testid="option.testId"
              >
                <div class="own-select__item-content">
                  <div
                    v-if="option.icon || $slots.left"
                    class="own-select__item-left"
                  >
                    <slot name="left" :item="option">
                      <Component
                        :is="option.icon"
                        v-if="option.icon"
                        class="flex-shrink-0"
                        size="24"
                      />
                    </slot>
                  </div>

                  <SelectItemText class="own-select__item-text">
                    <slot name="label" :item="option">
                      <OwnType
                        el="span"
                        :text="option.label"
                        :variant="
                          size === 'small'
                            ? 'paragraph-small'
                            : compact
                              ? 'paragraph-regular'
                              : 'title-regular'
                        "
                        color="inherit"
                        :title="option.label"
                      />
                    </slot>
                  </SelectItemText>

                  <div v-if="$slots.right" class="own-select__item-right">
                    <slot name="right" :item="option" />
                  </div>
                </div>
              </SelectItem>
            </SelectGroup>
          </template>

          <div v-else class="own-select__empty">
            <OwnType :text="t('labels.empty')" variant="title-regular" />
          </div>
        </SelectViewport>
      </SelectContent>
    </SelectPortal>
  </SelectRoot>
</template>

<script lang="ts" setup>
import { PhCaretDown } from '@phosphor-icons/vue'
import {
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectItemText,
  SelectLabel,
  SelectPortal,
  SelectRoot,
  SelectTrigger,
  SelectValue,
  SelectViewport,
} from 'reka-ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { OwnType } from '../OwnType'

import type { OwnSelectItem } from './types'

const props = withDefaults(
  defineProps<{
    accessibilityId?: string
    ariaLabel?: string
    compact?: boolean
    contentMinWidth?: string
    disabled?: boolean
    error?: boolean
    multiple?: boolean
    options: OwnSelectItem[]
    placeholder?: string
    size?: 'regular' | 'small'
    visibleOptions?: number
  }>(),
  {
    accessibilityId: undefined,
    ariaLabel: undefined,
    compact: false,
    contentMinWidth: undefined,
    disabled: false,
    error: false,
    multiple: false,
    placeholder: undefined,
    size: 'regular',
    visibleOptions: 4,
  }
)

const { t } = useI18n()

const modelValue = defineModel<string | number | (string | number)[]>()

const PADDING_HEIGHT = 8

const selectedValue = computed(() => {
  if (props.multiple && Array.isArray(modelValue.value)) {
    // For multiple selection, return the first selected option for display
    const firstValue = modelValue.value[0]
    return props.options.find((option) => option.value === firstValue)
  }
  return props.options.find((option) => option.value === modelValue.value)
})

const optionGroups = computed(() => {
  const groups = new Map<string, OwnSelectItem[]>()
  for (const option of props.options) {
    const key = option.group ?? ''
    groups.set(key, [...(groups.get(key) ?? []), option])
  }
  return Array.from(groups.entries()).map(([label, options]) => ({
    key: label || 'ungrouped',
    label,
    options,
  }))
})

const maxContentHeight = computed(
  () =>
    `${(props.size === 'small' ? 32 : props.compact ? 41 : 53) * props.visibleOptions + PADDING_HEIGHT}px`
)
</script>

<style lang="scss">
@use '@/styles/globals';

.own-select {
  width: 100%;
  position: relative;

  &__trigger {
    width: 100%;
    box-shadow: var(--shadow-card-default);
    background-color: var(--background-primary);
    cursor: pointer;
    outline: none;
    display: block;

    transition:
      globals.transitionOne(background-color), globals.transitionOne(border);

    &:hover,
    &:active,
    &:focus,
    &[data-state='open'] {
      background-color: var(--background-highlight);
      border-color: rgba(var(--raw-misc-brand), 0.05);
    }

    &--error {
      border-color: var(--status-danger);

      &:hover,
      &:active,
      &:focus {
        border-color: var(--status-danger);
      }
    }

    &--disabled,
    &--disabled:hover,
    &--disabled:active {
      cursor: not-allowed;
      color: var(--text-color-tertiary);
      background-color: var(--background-secondary);
      border-color: var(--divider);
    }
  }

  &__trigger-text-container {
    max-width: calc(100% - 24px);
  }

  &__content {
    background-color: var(--background-primary);
    border-radius: 8px;
    box-shadow: var(--shadow-card-default);
    padding: 4px;
    width: max-content !important;
    min-width: max-content !important;
    max-width: min(90vw, 36rem) !important;
    max-height: min(
      var(--max-content-height),
      var(--reka-select-content-available-height, var(--max-content-height))
    );
    overflow: hidden;
    z-index: 9999;
  }

  &__viewport {
    overflow-y: auto;
    max-height: 100%;
  }

  // Select item styling - matches OwnDropdownMenuItem exactly
  &__item {
    padding: 0;
    margin: 0;
    border-radius: 4px;
    background: transparent;
    border: none;
    outline: none;
    width: 100%;
    display: block;
    cursor: pointer;

    &[data-highlighted],
    &:hover {
      background-color: var(--background-highlight);
    }

    &[data-state='checked'] {
      background-color: var(--background-highlight);
    }

    &[data-disabled] {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__group-label {
    display: block;
    padding: 12px 16px 6px;
    color: var(--text-color-tertiary);
    font-size: 13px;
    font-weight: 600;
  }

  &__item-content {
    padding: 16px;
    display: flex;
    align-items: center;
    gap: 4px;
    width: max-content;
    max-width: 100%;
    transition: background-color 100ms ease-in;
  }

  &__trigger--small {
    min-height: 32px;
    padding: 6px 10px;
    border-radius: 8px;
    font-size: 13px;
    line-height: 18px;
  }

  &__content--compact &__item-content {
    padding: 10px 12px;
  }

  &__content--compact &__group-label {
    padding: 10px 12px 4px;
    font-size: 12px;
  }

  &__content--small &__item-content {
    min-height: 32px;
    padding: 6px 10px;
    font-size: 13px;
    line-height: 18px;
  }

  &__item-left {
    display: flex;
    align-items: center;
    flex-shrink: 0;

    &:empty {
      display: none;
    }
  }

  &__item-text {
    display: inline-flex;
    flex: 0 1 auto;
    min-width: 0;
    align-items: center;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    // Shrinking only engages once the content hits the popup's max-width cap,
    // so labels that fit stay untruncated.
    > * {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &__item-right {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  &__item-indicator {
    color: var(--color-primary);
    display: flex;
    align-items: center;

    // Only show when item is selected
    opacity: 0;
    transition: opacity 100ms ease-in;
  }

  // Show indicator when item is selected
  &__item[data-state='checked'] &__item-indicator {
    opacity: 1;
  }

  &__empty {
    padding: 8px;
    text-align: center;
    color: var(--color-secondary);
  }
}

[data-reka-popper-content-wrapper]:has(.own-select__content) {
  z-index: 301 !important;
  width: max-content !important;
  min-width: max-content !important;
  max-width: min(90vw, 36rem) !important;
}
</style>
